/**
 * @fileoverview "First to start: Tomato, Feb 18": which kept packet is first to start, and when, for plants starting
 * at Planning (D40). The backend's answer from Grow's planting dates for those crops at the garden's postcode on the
 * gardener's day, finished as a sentence (first.text); when none of them has dates, or the stage is not Planning, nothing is said. Moved out of
 * PacketReview for D78 (2026-10-22), unchanged, so the "Check and save" packet card says it too (rev 2 §3).
 *
 * Rows on Type "Not sure" (B52) have no type: their crops go to the server, which reads the crop's default type and
 * names the first to start itself (first.name, the crop in the reader's language; C118 F7).
 *
 * Where: PacketReview (above its button), PacketPile (above its button).
 * API: GET /plants/planting-windows?zip=&crops=&groups= (getCropPlantingDates); GET /garden-place
 * (useGardenPostcode: the garden's postcode and day, the owner's in a shared garden; audit A29, A31).
 * Settings: PLANTING_DATES_STALE_MS and PLANTING_CROP_DATES_QUERY_KEY (kept here, C118 F10).
 * Words: the server's (todo_word plants.packets.firstToStart and its "now" twin, W23 step 10), drawn as sent in first.text.
 *
 * @module useFirstToStart
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useGardenPostcode } from '@/shared/hooks/useGardenPostcode';
import { getCropPlantingDates } from '@/shared/api/plantingDates';
import type { PacketRowState } from './packetRows';

/**
 * How long the planting dates stay fresh. They change with the season, not the hour (the backend keeps them 12
 * hours). Milliseconds.
 * @kept K1
 */
const PLANTING_DATES_STALE_MS = 60 * 60 * 1000;

/**
 * The React Query key the planting dates of crops not yet in the garden (getCropPlantingDates) are cached under,
 * followed by the postcode and the crops: the seed-packet review sheet's "first to start".
 * @kept K1
 */
const PLANTING_CROP_DATES_QUERY_KEY = 'planting-crop-dates';

/**
 * The "First to start" line for the kept rows, or null.
 *
 * @param kept - The rows that will be filed.
 * @param atPlanning - The plants start at Planning.
 * @returns The line, or null when there is nothing to say.
 */
export function useFirstToStart(kept: readonly PacketRowState[], atPlanning: boolean): string | null {
  // The garden's postcode and its own day, the server's (the owner's in a shared garden; audit A29, A31).
  const { zip, usable, date: gardenDay } = useGardenPostcode();
  const keptCrops = [...new Set(kept.map((row) => row.choice.plantId).filter((id): id is number => id !== null))].sort((a, b) => a - b);
  // Rows on Type "Not sure" (B52) have no type: their crops go to the server, which reads the crop's default type.
  const unsureCrops = [...new Set(kept.map((row) => (row.choice.typeUnsure ? row.choice.cropId : null)).filter((id): id is number => id !== null))].sort((a, b) => a - b);
  // Which packet is first to start is the backend's answer for the garden's day, which is in the key so it turns over.
  const { data: cropAnswer } = useQuery({
    queryKey: [PLANTING_CROP_DATES_QUERY_KEY, zip, keptCrops.join(','), unsureCrops.join(','), gardenDay],
    queryFn: () => getCropPlantingDates(zip, keptCrops, unsureCrops),
    enabled: atPlanning && usable && (keptCrops.length > 0 || unsureCrops.length > 0),
    staleTime: PLANTING_DATES_STALE_MS,
    retry: false,
    placeholderData: keepPreviousData,
  });
  const first = atPlanning ? cropAnswer?.first ?? null : null;
  // The sentence is the server's, finished, in the reader's language (W23 step 10): the app picks no wording ("now" or a
  // date), formats no date and capitalises nothing. The server sends none for a crop it has no name for.
  return first?.text ?? null;
}
